import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useQuickAdd } from "@/lib/quickAdd";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Button, StatusPill, Select } from "@/components/kit";
import ConfirmDelete from "@/components/ConfirmDelete";
import { Txt } from "@/components/Txt";

const CODE_TONE = { active: "olive", redeemed: "gray", deleted: "maroon" };

export default function MembershipCodes() {
  const [codes, setCodes] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [form, setForm] = useState({ type: "full_waiver", percent: 25, reason: "" });
  const [pending, setPending] = useState(null);
  useQuickAdd("membership_codes", () => {
    const el = document.querySelector('[data-testid="create-code-card"]');
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    document.querySelector('[data-testid="code-type"]')?.focus();
  });

  const load = useCallback(async () => {
    const r = await api.get("/membership-codes");
    setCodes(r.data.codes);
    setReasons(r.data.waived_reasons);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.reason) return toast.error("Pick a reason.");
    const payload = { type: form.type, reason: form.reason };
    if (form.type === "percent_discount") payload.percent = Number(form.percent);
    try { const r = await api.post("/membership-codes", payload); toast.success(`Code ${r.data.code} created`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create code"); }
  };
  const del = (id) => setPending({ title: "Delete this code?", fn: async () => {
    try { await api.delete(`/membership-codes/${id}`); toast.success("Code deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  } });

  return (
    <div data-testid="membership-codes-page">
      <ConfirmDelete open={!!pending} onClose={() => setPending(null)} onConfirm={() => pending?.fn()} title={pending?.title} message="This permanently deletes the code." />
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold mb-1"><Txt id="membership.codes.title" screen="Membership · Codes">Membership codes</Txt></h1>
      <p className="text-[16px] text-ink/70 mb-5"><Txt id="membership.codes.subtitle" screen="Membership · Codes">Full-waiver or percentage-discount codes. Each is single-use by anyone who enters it.</Txt></p>

      <div className="bg-cream border border-ink/15 rounded-xl p-5 mb-5" data-testid="create-code-card">
        <h2 className="font-zilla text-xl font-semibold mb-3"><Txt id="membership.codes.generate_heading" screen="Membership · Codes">Generate a code</Txt></h2>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[13px] font-semibold block mb-1">Type</label>
            <Select data-testid="code-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]">
              <option value="full_waiver">Full Waiver</option>
              <option value="percent_discount">Percentage Discount</option>
            </Select>
          </div>
          {form.type === "percent_discount" && (
            <div>
              <label className="text-[13px] font-semibold block mb-1">Percent</label>
              <input data-testid="code-percent" type="number" min="1" max="100" value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} className="w-24 bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
            </div>
          )}
          <div>
            <label className="text-[13px] font-semibold block mb-1">Reason</label>
            <Select data-testid="code-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]">
              <option value="">Choose reason…</option>
              {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
            </Select>
          </div>
          <Button data-testid="create-code-btn" onClick={create}><Txt id="membership.codes.generate_btn" screen="Membership · Codes">Generate</Txt></Button>
        </div>
      </div>

      <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden" data-testid="codes-list">
        {codes.length === 0 && <div className="p-10 text-center text-ink/55 text-[16px]">No codes yet.</div>}
        {codes.map((c) => (
          <div key={c.code_id} data-testid={`code-row-${c.code}`} className="flex items-center justify-between gap-3 px-5 py-4 border-b border-ink/8 last:border-0">
            <div>
              <div className="font-mono text-[16px] font-semibold">{c.code}</div>
              <div className="text-[13px] text-ink/60">{c.type === "full_waiver" ? "Full waiver" : `${c.percent}% discount`} · {c.reason}{c.redeemed_by ? ` · redeemed by ${c.redeemed_by}` : ""}</div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <StatusPill tone={CODE_TONE[c.status] || "gray"} className="text-[12px] px-2.5 py-1 capitalize">{c.status}</StatusPill>
              {c.status === "active" && <button data-testid={`delete-code-${c.code}`} onClick={() => del(c.code_id)} className="text-maroon text-[13px] font-semibold hover:underline">Delete</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
